import {Injectable} from '@angular/core';

/**
 * Choix de l'utilisateur, tel que le bandeau cookies du site vitrine l'enregistre.
 * Une clé par catégorie proposée dans le bandeau.
 */
export interface CookieConsent {
  necessary?: boolean;
  analytics?: boolean;
  ads?: boolean;
  videos?: boolean;
  social?: boolean;
  all?: boolean;
}

/**
 * Lit le consentement cookies collecté par le bandeau de https://coucouvisio.fr.
 *
 * Le bandeau vit sur le site vitrine ; l'application n'en affiche aucun. Le site dépose le choix
 * dans un cookie porté par le domaine `.coucouvisio.fr`, donc lisible depuis `app.coucouvisio.fr`
 * — son `localStorage`, lui, ne l'est pas : les deux origines sont distinctes.
 *
 * Pas de cookie = pas de consentement = refus. C'est aussi l'état en local et en démo, où
 * l'application ne tourne pas sous `coucouvisio.fr`.
 */
@Injectable({
  providedIn: 'root'
})
export class ConsentService {

  /** Nom du cookie écrit par le bandeau du site vitrine. */
  static readonly CONSENT_COOKIE = 'cookie-consent';

  /** L'utilisateur a-t-il accepté la mesure d'audience (Google Analytics & Clarity) ? */
  get analyticsGranted(): boolean {
    return this.read()?.analytics === true;
  }

  /**
   * L'utilisateur a-t-il accepté la publicité (Google Ads : conversions essai gratuit et abonnement) ?
   * Un cookie écrit avant l'arrivée de cette catégorie dans le bandeau n'a pas la clé `ads` — même
   * avec `all` à `true` — et vaut donc refus.
   */
  get adsGranted(): boolean {
    return this.read()?.ads === true;
  }

  read(): CookieConsent | null {
    const value = this.readCookie(ConsentService.CONSENT_COOKIE);

    if (!value) {
      return null;
    }

    try {
      return JSON.parse(decodeURIComponent(value)) as CookieConsent;
    } catch {
      return null;
    }
  }

  private readCookie(name: string): string | null {
    const cookie = document.cookie
      .split(';')
      .map(entry => entry.trim())
      .find(entry => entry.startsWith(`${name}=`));

    return cookie ? cookie.substring(name.length + 1) : null;
  }
}
